import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import PageTabs from '../shared/PageTabs';
import { buildLPathTabs } from '../shared/tabConfigs';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Chip,
  Paper,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PreviewIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Publish, Refresh, Save, Search, FilterList } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  PlayCircleOutline,
  Description,
  Assignment,
  Quiz,
  MenuBook,
  AccountTree,
  CheckCircle,
  Info,
  Warning
} from '@mui/icons-material';
import { learningPathService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import '../Course/CourseGrid.css';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EmptyState from '../common/EmptyState';

// Styled components to match LearningPathModules.jsx
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const ContentThumbnail = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '8px',
  marginRight: theme.spacing(2),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f0f0f0',
  border: '1px solid #ddd'
}));

// Custom CSS for hover effects
const customStyles = `
  .preview-button {
    background-color: #2563eb;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: bold;
    margin-top: 16px;
  }
  
  .preview-button:hover {
    background-color: #c01052;
  }
  
  .course-card .hover-overlay {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  
  /* Fix iframe scrolling */
  .preview-iframe-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  
  .preview-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
  
  .page-header {
    padding: 16px 0;
    margin-bottom: 24px;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .content-accordion {
    margin-bottom: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .content-accordion.completed-accordion {
    background-color: rgba(76, 175, 80, 0.08);
  }
  
  .content-accordion-summary {
    min-height: 80px;
  }
  
  .content-accordion-summary:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  .completed-accordion .content-accordion-summary:hover {
    background-color: rgba(76, 175, 80, 0.12);
  }
  
  .settings-panel {
    background-color: #f9f9f9;
    border-left: 1px solid #e0e0e0;
    padding: 24px;
  }
  
  .drag-handle {
    cursor: grab;
    color: #aaa;
    margin-right: 12px;
  }
  
  .drag-handle:hover {
    color: #666;
  }
  
  .course-card-container {
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: white;
    transition: background-color 0.2s ease;
  }
  
  .course-card-container:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  /* Add styles for disabled learning paths */
  .disabled-learning-path {
    opacity: 0.6;
    background-color: #f5f5f5;
    pointer-events: none;
  }

  .disabled-learning-path:hover {
    background-color: #f0f0f0;
  }

  .info-message {
    padding: 8px 16px;
    background-color: #f5f5f5;
    border-radius: 4px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

// Utility function to get content icon based on type
const getContentIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'video':
      return <PlayCircleOutline />;
    case 'document':
      return <Description />;
    // case 'assignment':
    //   return <Assignment />;
    //  case 'quiz':
    //   return <Quiz />;
    case 'course':
      return <MenuBook color="primary" />;
    case 'learning path':
      return <AccountTree color="success" />;
    default:
      return <Description />;
  }
};

// Utility function to get content type color
const getContentTypeColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'course':
      return '#1976d2';
    case 'learning path':
      return '#4caf50';
    default:
      return '#757575';
  }
};

// Helper function to check if a learning path has nested learning paths
const hasNestedLearningPaths = (component) => {
  return component.type === 'learning path' && component.nested_lpath_count > 0;
};

// Custom version of ItemCard with simplified menu
const ContentSelectorCard = ({ item, onPreview }) => {
  // Check if this is a learning path with nested learning paths
  const hasNestedPaths = item.type === 'learning path' && item.nested_lpath_count > 0;

  return (
    <div className="course-card-wrapper">
      <div className="course-card" onClick={() => !hasNestedPaths && onPreview(item)}>

        {/* Add an overlay for nested learning paths */}
        {hasNestedPaths && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.1)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexDirection: 'column',
            padding: '16px',
            textAlign: 'center'
          }}>
            <Info style={{ fontSize: 40 }} />
            <Typography variant="body2" style={{ marginTop: 8, color: '#333', fontWeight: 'bold' }}>
              Contains nested learning paths
            </Typography>
            <Typography variant="caption" style={{ marginTop: 4 }}>
              Cannot be selected
            </Typography>
          </div>
        )}

        <div className="hover-overlay">
          <Box className="course-description">
            <Typography
              component="div"
              className="card-description"
              dangerouslySetInnerHTML={{
                __html: item.description?.replace(/<\/?p>/g, '')
              }}
            />
          </Box>
          {/* Only show preview button if the item doesn't have nested paths */}
          {!hasNestedPaths && (
            <button className="preview-button" onClick={(e) => {
              e.stopPropagation();
              onPreview(item);
            }}>
              <PreviewIcon fontSize="small" /> Preview
            </button>
          )}
        </div>
        <div className={`status-badge ${'status-published'}`}>
          {'Published'}
        </div>
        
        <img
          src={item.image_url
            ? `/uploads/${item.type === 'course' ? 'courses' : 'learning_paths'}/${item.image_url}`
            : '/images/nopreview.png'}
          alt={item.title}
          className="card-image"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/images/nopreview.png';
          }}
        />
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            backgroundColor: '#F1EFE8',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
            mr: 2,
            color: 'rgba(22,21,15,.50)',                      
          }}
        >
          {item.type === 'course' ? 'C' : 'LP'}
        </Box>
      </div>
      <div className="card-footer">
        <div>
          <h3 className="card-title" style={{ marginBottom: '4px' }}>
            {item.title}
          </h3>
          
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              lineHeight: 1.2
            }}
          >
            {item.type === 'course' ? 'Course' : 'Learning Path'}
          </Typography>
          
        </div>
        <div className="course-card-menu-wrapper">
          <div className="course-card-menu-dropdown">
            <div className="course-card-menu-item" onClick={() => onPreview(item)}>
              <PreviewIcon className="drop-menu-icon" /> Preview
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sortable content component
const SortableContent = ({ content, onPreview, onDelete, isRequired, onToggleRequired }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: `${content.type}-${content.id}` });



  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const contentId = `${content.type}-${content.id}`;
  const coverImage = content.cover_picture || content.image_url
    ? `/uploads/${content.type === 'course' ? 'courses' : 'learning_paths'}/${content.cover_picture || content.image_url}`
    : `/assets/images/course-default.jpg`;
  const typeColor = getContentTypeColor(content.type);
  const isLearningPath = content.type === 'learning path';
  const [hasImageError, setHasImageError] = useState(false);
  const contentKey = `${content.type}-${content.id}`;
  return (
    <div ref={setNodeRef} style={style}>
      <Box
        className="course-card-container"
      >
        <DragIndicatorIcon
          className="drag-handle"
          {...attributes}
          {...listeners}
        />
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            backgroundColor: '#F1EFE8',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
            mr: 2,
            color: 'rgba(22,21,15,.50)',                      
          }}
        >
          {content.type === 'course' ? 'C' : 'LP'}
        </Box>
        {/*
        <ContentThumbnail>
          {hasImageError ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: typeColor + '20'
            }}>
              {getContentIcon(content.type)}
            </Box>
          ) : (
            <Box
              component="img"
              src={coverImage}
              alt={content.title || content.name}
              className="content-thumbnail-img"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={() => setHasImageError(true)}
            />
          )}
        </ContentThumbnail>
        */}
        
        <Box sx={{ flexGrow: 1, ml: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ fontWeight: 500, mr: 1 }} className="content-title">
              {content.title || content.name}
            </Typography>
            {isRequired && (
              <Chip
                label="Required"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 24, mr: 1 }}
              />
            )}
          </Box>
          {/*
          <Typography
            variant="caption"
            sx={{
              color: typeColor,
              fontWeight: 500,
              display: 'inline-block',
              border: `1px solid ${typeColor}`,
              borderRadius: '8px',
              padding: '0 6px',
              marginTop: '4px'
            }}
          >
            {isLearningPath ? 'Learning Path' : 'Course'}
          </Typography>
          */}
          {content.has_prerequisites && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mt: 0.5 }}
            >
              Heads up! This course requires prerequisites. Learners will need to complete them before resuming the learning path.
            </Typography>
          )}

          {content.description && (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, maxHeight: '40px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {content.description?.replace(/<\/?p>/g, '').substring(0, 100)}
              {content.description?.length > 100 ? '...' : ''}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isRequired}
                onChange={(e) => onToggleRequired(contentKey, e.target.checked)}
                color="primary"
                size="small"
                sx={{ mt: 0 }}
              />
            }
            label="Required"
            sx={{ mr: 2}}
          />
          <Tooltip title="Remove">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(content.id, content.type)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </div>
  );
};

const LPathContentSelector = ({ onContentChange, learningPathName }) => {
  // Extract the ID from URL parameters
  const { id } = useParams();
  const { user } = useUser();
  const lpathTabs = buildLPathTabs(id, { permissions: user?.permissions || [], roleId: user?.role_id });
  const learningPathId = id;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [components, setComponents] = useState([]);
  const [selectedContents, setSelectedContents] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [addedContents, setAddedContents] = useState([]);
  const [loadingAddedContents, setLoadingAddedContents] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [requireAllModules, setRequireAllModules] = useState(false);
  const [requiredCount, setRequiredCount] = useState(0);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pathTitle, setPathTitle] = useState(learningPathName || 'Learning Path Content');
  const [publishStatus, setPublishStatus] = useState('draft'); // 'draft', 'published'
  const [isPublishing, setIsPublishing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [learningPathAudienceType, setLearningPathAudienceType] = useState('3');

  const [pendingChanges, setPendingChanges] = useState({
    contentOrder: false,
    requiredCount: false
  });
  const { showToast } = useToast();

  // Use a ref to track the current page number
  const currentPageRef = useRef(0);
  const loadedIdsRef = useRef(new Set());
  const scrollContainerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const ITEMS_PER_PAGE = 12;

  const [requiredModules, setRequiredModules] = useState({});

  // Setup DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch the list of already added contents when component mounts
  useEffect(() => {
    if (learningPathId) {
      fetchLPathSettings();
    }
  }, [learningPathId]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce time

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  // Update fetchData to include search and filter
  useEffect(() => {
    if (open && debouncedSearchQuery !== undefined) {
      // Reset pagination and fetch with new search/filter
      setComponents([]);
      currentPageRef.current = 0;
      loadedIdsRef.current = new Set();
      setHasMore(true);
      fetchData(0);
    }
  }, [debouncedSearchQuery, contentTypeFilter, open, learningPathAudienceType]);

  const fetchLPathSettings = async () => {
    if (!learningPathId) return;

    try {
      setLoadingAddedContents(true);
      const response = await learningPathService.getLPDetails(learningPathId);

      if (response.data && response.data.status === 'success') {
        const lpathData = response.data.data || {};

        // Set learning path name if available
        if (lpathData.lPath.title) {
          setPathTitle(lpathData.lPath.title);
        }

        // remember audience so we can filter later
        if (lpathData.lPath.audience_type) {
          setLearningPathAudienceType(String(lpathData.lPath.audience_type));
        }

        // Set publish status
        if (lpathData.lPath.status) {
          setPublishStatus(lpathData.lPath.status === '3' ? 'published' : 'draft');
        }

        // Extract and process path contents
        const pathContents = lpathData.path_contents || {};

        const contentsArray = Object.entries(pathContents).map(([key, value]) => {
          const [id, type] = key.split('|');
          return {
            id: parseInt(id),
            type: type,
            title: value.title,
            description: value.description || '',
            cover_picture: value.content_info?.cover_picture || '',
            lpcr_id: value.lpcr_id,
            required: value.required === '1',
            has_prerequisites: value.has_prerequisites || false
          };
        });

        setAddedContents(contentsArray);

        // Set requirement settings
        const totalContents = contentsArray.length;
        const reqCount = parseInt(lpathData.lPath.required_count) || 0;
        setRequiredCount(reqCount);
        // Initialize requiredModules state
        const initialRequiredModules = {};

        contentsArray.forEach(content => {
          const contentKey = `${content.type}-${content.id}`;
          initialRequiredModules[contentKey] = content.required;
        });

        setRequiredModules(initialRequiredModules);

        // Set requireAllModules flag if all modules are required
        const requiredModulesCount = Object.values(initialRequiredModules).filter(Boolean).length;
        setRequireAllModules(requiredModulesCount === totalContents && totalContents > 0);
      }
    } catch (error) {
      console.error('Error fetching learning path settings:', error);
      showToast('Error loading learning path data', 'error');
    } finally {
      setLoadingAddedContents(false);
    }
  };

  const fetchAddedContents = async () => {
    if (!learningPathId) return;

    try {
      setLoadingAddedContents(true);
      const response = await learningPathService.getLPathContents(learningPathId);

      if (response.data && response.data.success) {
        setAddedContents(response.data.contents || []);
      } else {
        console.error('API returned unsuccessful response:', response.data);
        showToast('Error loading added contents', 'error');
      }
    } catch (error) {
      console.error('Error fetching added contents:', error);
      showToast('Error loading added contents', 'error');
    } finally {
      setLoadingAddedContents(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setComponents([]);
    currentPageRef.current = 0;
    loadedIdsRef.current = new Set();
    setHasMore(true);
    fetchData(0);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedContents([]);
    setPreviewOpen(false);
    setPreviewItem(null);
  };

  const handleImageError = (contentId) => {
    setImageErrors(prev => ({
      ...prev,
      [contentId]: true
    }));
  };

  // Function to fetch data with proper pagination
  const fetchData = async (page) => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setLoading(true);

      // Calculate the start position based on page number
      const start = page * ITEMS_PER_PAGE;

      // Create the request payload
      const requestPayload = {
        lpathId: learningPathId,
        start: start,
        length: ITEMS_PER_PAGE,
        offset: start,
        limit: ITEMS_PER_PAGE,
        draw: page + 1,
        search: debouncedSearchQuery || '',
        contentType: contentTypeFilter !== 'all' ? contentTypeFilter : '',
        // send the selected audience type to the server; default to '3' (both) if not set
        audience_type: learningPathAudienceType || '3'
      };

      // Make the API call
      const response = await learningPathService.lpathComponentsList(requestPayload);

      if (response.data && response.data.success) {
        const newComponents = response.data.components || [];

        // Update total count if available
        if (response.data.total !== undefined) {
          setTotalCount(response.data.total);
        }

        // Mark components that are already added
        const processedComponents = newComponents.map(component => {
          const isAdded = addedContents.some(item =>
            item.id === component.id && item.type === component.type
          );

          // Make sure nested_lpath_count is accessible
          const nested_lpath_count = component.nested_lpath_count || 0;

          return {
            ...component,
            isAdded,
            nested_lpath_count,
            hasNestedPaths: component.type === 'learning path' && nested_lpath_count > 0
          };
        });

        // Filter out duplicates using the loadedIds Set
        const uniqueNewComponents = processedComponents.filter(component => {
          const componentKey = `${component.type}-${component.id}`;
          if (loadedIdsRef.current.has(componentKey)) {
            return false;
          }
          loadedIdsRef.current.add(componentKey);
          return true;
        });

        // Append or replace components based on page number
        if (page === 0) {
          setComponents(uniqueNewComponents);
        } else {
          setComponents(prev => [...prev, ...uniqueNewComponents]);
        }

        // Determine if there are more items to load
        const hasMoreItems = response.data.hasMore !== undefined
          ? response.data.hasMore
          : uniqueNewComponents.length >= ITEMS_PER_PAGE;

        setHasMore(hasMoreItems && uniqueNewComponents.length > 0);

        // Update the current page ref for the next fetch
        currentPageRef.current = page + 1;
      } else {
        console.error('API returned unsuccessful response:', response.data);
        showToast('Error loading components', 'error');
      }
    } catch (error) {
      console.error('Error fetching components:', error);
      showToast('Error loading components', 'error');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Handle scroll event for infinite scrolling
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (!scrollContainerRef.current || isLoadingRef.current || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const scrollPosition = scrollHeight - scrollTop - clientHeight;

      // Load more when near the bottom (within 100px)
      if (scrollPosition < 100) {
        fetchData(currentPageRef.current);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [open, hasMore, components.length, totalCount]);

  // Manual load more function
  const handleLoadMore = () => {
    fetchData(currentPageRef.current);
  };

  const handleAddContents = async () => {
    try {
      const contentData = {
        lpath_id: learningPathId,
        selected_contents: selectedContents.map(item => `${item.id}|${item.type}`)
      };

      const response = await learningPathService.addContent(contentData);

      if (response.data) {
        onContentChange && onContentChange(response.data);
        handleClose();
        showToast('Content added successfully', 'success');
        // Refresh the list of added contents
        fetchLPathSettings();

        // Mark content order as changed
        setPendingChanges(prev => ({
          ...prev,
          contentOrder: true
        }));
      }
    } catch (error) {
      console.error('Error adding content:', error);
      showToast('Error adding content', 'error');
    }
  };

  const handlePreview = (item) => {
    setPreviewItem(null);
    setPreviewLoading(true);
    setTimeout(() => {
      setPreviewItem(item);
      setPreviewOpen(true);
    }, 50);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
  };

  const handleSelectContent = (content) => {
    // Skip if the content is already added to the learning path    
    if (addedContents.some(item => item.id === content.id && item.type === content.type)) {
      return;
    }

    // Skip if this is a learning path with nested learning paths
    if (content.type === 'learning path' && content.nested_lpath_count > 0) {
      showToast('Learning paths containing other learning paths cannot be selected', 'warning');
      return;
    }

    setSelectedContents(prev => {
      const isSelected = prev.some(item =>
        item.id === content.id && item.type === content.type
      );

      if (isSelected) {
        return prev.filter(item =>
          !(item.id === content.id && item.type === content.type)
        );
      } else {
        return [...prev, content];
      }
    });
  };

  const isContentSelected = (content) => {
    // Check if it's in the current selection
    const isInCurrentSelection = selectedContents.some(item =>
      item.id === content.id && item.type === content.type
    );

    // Check if it's already added to the learning path
    const isAlreadyAdded = addedContents.some(item =>
      item.id === content.id && item.type === content.type
    );

    return isInCurrentSelection || isAlreadyAdded;
  };

  const handleRemoveContent = async (content) => {
    try {
      const contentId = content.id;
      const contentType = content.type;
      const lpcrId = content.lpcr_id;

      // Show loading state if needed
      // setLoadingRemove(true);

      const response = await learningPathService.removeContent({
        lpath_id: learningPathId,
        custom_id: `${contentId}|${contentType}`,
        lpcr_id: lpcrId
      });

      if (response.data && response.data.success) {
        // Update the UI state directly by filtering out the removed content
        setAddedContents(prevContents =>
          prevContents.filter(item => item.lpcr_id !== lpcrId)
        );

        // Update any other state that might depend on the content count
        if (requireAllModules) {
          // If "require all modules" is enabled, update the required count
          setRequiredCount(prev => Math.max(0, prev - 1));
        }

        // Show success message
        showToast('Content removed successfully', 'success');

        // Notify parent component if needed
        onContentChange && onContentChange({
          success: true,
          action: 'remove',
          removed: { id: contentId, type: contentType, lpcr_id: lpcrId }
        });

        // Mark content order as changed if needed
        setPendingChanges(prev => ({
          ...prev,
          contentOrder: true
        }));
      } else {
        showToast('Error removing content', 'error');
      }
    } catch (error) {
      console.error('Error removing content:', error);
      showToast('Error removing content', 'error');
    } finally {
      // Clear loading state if needed
      // setLoadingRemove(false);
    }
  };

  const handleRequireAllChange = (event) => {
    const checked = event.target.checked;
    setRequireAllModules(checked);

    if (checked) {
      // If requiring all modules, set required count to total number of modules
      setRequiredCount(addedContents.length);
    }

    // Mark required count as changed
    setPendingChanges(prev => ({
      ...prev,
      requiredCount: true
    }));
  };

  const handleRequiredCountChange = (event) => {
    setRequiredCount(event.target.value);

    // Mark required count as changed
    setPendingChanges(prev => ({
      ...prev,
      requiredCount: true
    }));
  };

  // Batch update function to save multiple changes at once
  const saveBatchChanges = async () => {
    return await updateLearningPathSettings({
      successMessage: 'Changes saved successfully'
    });
  };

  /**
   * Unified function to update learning path settings
   * @param {Object} options - Configuration options
   * @param {string} options.status - Status to set (0=draft, 3=published)
   * @param {boolean} options.showToast - Whether to show toast notifications
   * @param {string} options.successMessage - Message to show on success
   * @returns {Promise<boolean>} - Success status
   */
  const updateLearningPathSettings = async (options = {}) => {
    const {
      status,
      showToast: shouldShowToast = true,
      successMessage = 'Learning path updated successfully'
    } = options;

    try {
      // Set loading state based on the operation
      if (status === '3') {
        setIsPublishing(true);
      } else {
        setSavingSettings(true);
      }

      // Prepare the update data
      const updateData = {};

      // Add status if provided
      if (status !== undefined) {
        updateData.status = status;
      }

      // Always include required flag and count
      updateData.required = requiredCount > 0 ? '1' : '0';
      updateData.required_count = requiredCount;

      // Always include the content order
      updateData.content_order = addedContents.map((item, index) => ({
        id: item.id,
        type: item.type,
        order: index + 1,
        lpcr_id: item.lpcr_id
      }));

      // Always include required modules
      updateData.required_modules = addedContents
        .filter(content => requiredModules[`${content.type}-${content.id}`])
        .map(content => ({
          id: content.id,
          type: content.type,
          lpcr_id: content.lpcr_id
        }));

      // Make the API call
      const response = await learningPathService.updateSettings(learningPathId, updateData);

      if (response.data && response.data.success) {
        // Update UI state
        if (status === '3') {
          setPublishStatus('published');
        } else if (status === '1') {
          setPublishStatus('draft');
        }

        // Reset pending changes
        setPendingChanges({
          contentOrder: false,
          requiredCount: false
        });

        // Show success message if requested
        if (shouldShowToast) {
          showToast(successMessage, 'success');
        }

        return true;
      } else {
        if (shouldShowToast) {
          showToast('Error updating learning path', 'error');
        }
        return false;
      }
    } catch (error) {
      console.error('Error updating learning path:', error);
      if (shouldShowToast) {
        showToast('Error updating learning path', 'error');
      }
      return false;
    } finally {
      // Reset loading states
      setIsPublishing(false);
      setSavingSettings(false);
    }
  };
  // Handle publishing the learning path
  const handlePublish = async () => {
    // Validate that at least one module is required
    if (requiredCount === 0) {
      showToast('At least one module must be required to publish', 'error');
      return;
    }

    // Validate that there is at least one module
    if (addedContents.length === 0) {
      showToast('Add at least one module before publishing', 'error');
      return;
    }

    await updateLearningPathSettings({
      status: '3',
      successMessage: 'Learning path published successfully'
    });
  };

  // // Handle drag end for reordering content
  // const handleDragEnd = (event) => {
  //   const { active, over } = event;

  //   if (!active || !over || active.id === over.id) return;

  //   setAddedContents((items) => {
  //     const oldIndex = items.findIndex(item => `${item.type}-${item.id}` === active.id);
  //     const newIndex = items.findIndex(item => `${item.type}-${item.id}` === over.id);

  //     if (oldIndex === -1 || newIndex === -1) return items;

  //     const newOrder = arrayMove(items, oldIndex, newIndex);

  //     // Mark content order as changed
  //     setPendingChanges(prev => ({
  //       ...prev,
  //       contentOrder: true
  //     }));

  //     return newOrder;
  //   });
  // };

const handleDragEnd = async (event) => {
  const { active, over } = event;
  if (!active || !over || active.id === over.id) return;

  setAddedContents((items) => {
    const oldIndex = items.findIndex(item => `${item.type}-${item.id}` === active.id);
    const newIndex = items.findIndex(item => `${item.type}-${item.id}` === over.id);

    if (oldIndex === -1 || newIndex === -1) return items;

    const newOrder = arrayMove(items, oldIndex, newIndex);

    // Mark content order as changed
    setPendingChanges(prev => ({
      ...prev,
      contentOrder: true
    }));

    // After updating local state, send order to backend
    // We do this here inside setAddedContents callback (or you can do it after setAddedContents)
    (async () => {
      try {
        // Prepare payload for API: must include lpcr_id and sequence
        const payloadElements = newOrder.map((content, index) => ({
          lpcr_id: content.lpcr_id,
          sequence: index + 1,
          element_type: content.type,  // if your API needs it (optional if backend uses lpcr_id only)
          element_id: content.id        // optional if backend needs
        }));

        await learningPathService.updateOrder(learningPathId, payloadElements);
        console.log('Sequence updated in DB!');
      } catch (error) {
        console.error('Failed to update sequence order:', error);
        // Optionally rollback or notify user
      }
    })();

    return newOrder;
  });
};


  const handleToggleRequired = (contentKey, isRequired) => {
    setRequiredModules(prev => {
      const updated = { ...prev, [contentKey]: isRequired };

      // Count how many modules are now required
      const requiredCount = Object.values(updated).filter(Boolean).length;

      // Update the requiredCount state
      setRequiredCount(requiredCount);

      // Update requireAllModules state
      setRequireAllModules(requiredCount === addedContents.length && requiredCount > 0);

      // Mark required count as changed
      setPendingChanges(prev => ({
        ...prev,
        requiredCount: true
      }));

      return updated;
    });
  };

  const handleToggleAllRequired = (setAll) => {
    const updated = {};

    addedContents.forEach(content => {
      const key = `${content.type}-${content.id}`;
      updated[key] = setAll;
    });

    setRequiredModules(updated);
    setRequiredCount(setAll ? addedContents.length : 0);
    setRequireAllModules(setAll);

    // Mark required count as changed
    setPendingChanges(prev => ({
      ...prev,
      requiredCount: true
    }));
  };

  const handleDeleteContent = (contentKey) => {
    setAddedContents(prev => prev.filter(item => `${item.type}-${item.id}` !== contentKey));

    // Mark content order as changed
    setPendingChanges(prev => ({
      ...prev,
      contentOrder: true
    }));
  };

  return (
    <Box>
      {/* Inject custom styles */}
      <style>{customStyles}</style>
      <PageTabs tabs={lpathTabs} />
      {/* Page Header with Learning Path Name and Action Buttons */}
      <Box className="page-header" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {learningPathName || 'Learning Path Content'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Save to Draft Button */}
          <Button
            variant="outlined"
            color="primary"
            onClick={async () => {
              await updateLearningPathSettings({
                status: '1',
                successMessage: 'Learning path saved as draft'
              });
              setTimeout(() => {
                navigate(`/learning_path`);
              }, 1000);
            }}
            disabled={savingSettings || isPublishing || addedContents.length === 0}
            startIcon={<Save />}
            sx={{ minWidth: '140px', height: '40px' }}
          >
            {savingSettings ? <CircularProgress size={24} color="inherit" /> : 'Draft'}
          </Button>

          {/* Publish/Republish Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              // Validate that at least one module is required
              if (requiredCount === 0) {
                showToast('At least one module must be required to publish', 'error');
                return;
              }

              // Validate that there is at least one module
              if (addedContents.length === 0) {
                showToast('Add at least one module before publishing', 'error');
                return;
              }

              await updateLearningPathSettings({
                status: '3', // Published status
                successMessage: 'Learning path published successfully'
              });
              setTimeout(() => {
                navigate(`/learning_path`);
              }, 1000);
            }}
            disabled={
              isPublishing ||
              savingSettings ||
              addedContents.length === 0 ||
              requiredCount === 0
            }
            startIcon={publishStatus === 'published' ? <Refresh /> : <Publish />}
            sx={{ minWidth: '140px', height: '40px' }}
          >
            {isPublishing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              publishStatus === 'published' ? 'Republish' : 'Publish'
            )}
          </Button>
        </Box>
      </Box>


      <Grid container spacing={3}>
        {/* Main Content Area */}
        <Grid item xs={12} md={8}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpen}
            sx={{ mb: 3 }}
          >
            Add Courses/Learning Paths
          </Button>

          <Box elevation={1}>
            <Typography variant="h5" component="h2" gutterBottom>
              Learning Path Modules
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {loadingAddedContents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : addedContents.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={addedContents.map(content => `${content.type}-${content.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {!requireAllModules && requiredCount > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Chip
                        icon={<Info />}
                        label={`${requiredCount} of ${addedContents.length} modules required`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </Box>
                  )}

                  {/* Add a summary chip if requiring all modules */}
                  {requireAllModules && addedContents.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Chip
                        icon={<Info />}
                        label="All modules required"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </Box>
                  )}

                  {addedContents.map((content, index) => {
                    const contentKey = `${content.type}-${content.id}`;
                    const isRequired = requiredModules[contentKey] || false;
                    return (
                      <SortableContent
                        key={`${content.type}-${content.id}`}
                        content={content}
                        onPreview={handlePreview}
                        onDelete={() => handleRemoveContent(content)}
                        index={index}
                        isRequired={isRequired}
                        onToggleRequired={handleToggleRequired}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            ) : (
              <>
                {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                <EmptyState title="No content has been added to this learning path yet." message={null} />
              </>
            )}
          </Box>
        </Grid>

        {/* Settings Panel */}
        <Grid item xs={12} md={4}>
          <SectionPaper elevation={1} className="settings-panel">
            <Typography variant="h5" component="h2" gutterBottom>
              Learning Path Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Completion Requirements Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Completion Requirements
              </Typography>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={requiredCount > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // When enabling requirements, default to "All" modules
                          handleToggleAllRequired(true);
                        } else {
                          // When disabling requirements, set count to 0
                          handleToggleAllRequired(false);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label="Require specific modules to be completed"
                />
                {requiredCount > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      {requiredCount} of {addedContents.length} modules required
                    </Typography>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleToggleAllRequired(!requireAllModules)}
                      disabled={addedContents.length === 0}
                    >
                      {requireAllModules ? 'Unselect All' : 'Select All'}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Validation warning if no modules are required */}
              {requiredCount === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  At least one module must be required to publish this learning path.
                </Alert>
              )}

              {/* Unsaved changes indicator */}
              {(pendingChanges.requiredCount || pendingChanges.contentOrder) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You have unsaved changes. Click the {publishStatus === 'published' ? 'Republish' : 'Publish'} button at the top to save your changes.
                </Alert>
              )}
            </Box>
          </SectionPaper>
        </Grid>

      </Grid>

      {/* Content Selection Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {!previewOpen ? (
            "Add Content to Learning Path"
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handlePreviewClose} edge="start" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">
                {previewItem?.type === 'course' ? 'Course Preview' : 'Learning Path Preview'}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: previewOpen ? 0 : 3 }}>
          {!previewOpen ? (
            <Box
              ref={scrollContainerRef}
              sx={{ height: 'calc(100vh - 250px)', overflow: 'auto' }}
            >
              {/* Add this informational message */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="info" fontSize="small" />
                <Typography variant="body2">
                  Learning paths containing other learning paths are disabled and cannot be selected.
                </Typography>
              </Box>

              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label=" Content"
                  variant="outlined"
                  // fullWidth
                  sx={{ minWidth: 700 }}
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="end">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="content-type-filter-label">Content Type</InputLabel>
                  <Select
                    labelId="content-type-filter-label"
                    id="content-type-filter"
                    value={contentTypeFilter}
                    onChange={(e) => setContentTypeFilter(e.target.value)}
                    label="Content Type"
                    startAdornment={
                      <InputAdornment position="end">
                        <FilterList />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="course">Courses</MenuItem>
                    <MenuItem value="learning path">Learning Paths</MenuItem>
                  </Select>
                </FormControl>

                {(searchQuery || contentTypeFilter !== 'all') && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt:2,ml: 3 }}
                    onClick={() => {
                      setSearchQuery('');
                      setContentTypeFilter('all');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              <Grid container spacing={3}>
                {components.map((component, index) => {
                  const hasNestedPaths = component.type === 'learning path' && component.nested_lpath_count > 0;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={`${component.type}-${component.id}-${index}`}>
                      <Box sx={{ position: 'relative' }}>
                        {/* Checkbox on the left top corner without background */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isContentSelected(component)}
                              onChange={() => handleSelectContent(component)}
                              disabled={component.isAdded}
                              sx={{
                                color: '#2563eb',
                                '&.Mui-checked': {
                                  color: '#2563eb',
                                }
                              }}
                            />
                          }
                          label=""
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 10,
                            margin: 0,
                            padding: '8px'
                          }}
                        />

                        <ContentSelectorCard
                          item={component}
                          onPreview={handlePreview}
                        />

                        {component.isAdded && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              right: 8,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem'
                            }}
                          >
                            Already Added
                          </Box>
                        )}

                        {hasNestedPaths && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              right: 8,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem'
                            }}
                          >
                            Contains Nested Paths
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  );
                })}

                {/* Loading indicator */}
                {loading && (
                  <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={30} />
                  </Grid>
                )}

                {/* No results message */}
                {!loading && components.length === 0 && (
                  <Grid item xs={12}>
                    {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
                    <EmptyState title="No courses or learning paths available" message={null} />
                  </Grid>
                )}

                {/* Load more button */}
                {hasMore && !loading && components.length > 0 && (
                  <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleLoadMore}
                    >
                      Load More ({components.length} of {totalCount || '?'})
                    </Button>
                  </Grid>
                )}

                {/* End of list message */}
                {!hasMore && components.length > 0 && (
                  <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      End of list - {components.length} items loaded
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ height: 'calc(100vh - 200px)', overflow: 'hidden' }}>
              <Box sx={{ position: 'relative', height: '100%' }}>
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: previewLoading ? 10 : -1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255,255,255,0.8)'
                }}>
                  <CircularProgress />
                </Box>
                {previewItem && (
                  <div className="preview-iframe-container">
                    <iframe
                      className="preview-iframe"
                      src={previewItem.type === 'course'
                        ? `/course/preview/${previewItem.id}/standalone`
                        : `/learning_path/preview/${previewItem.id}/standalone`
                      }
                      onLoad={() => setPreviewLoading(false)}
                      title={previewItem.type === 'course' ? 'Course Preview' : 'Learning Path Preview'}
                      scrolling="auto"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        {!previewOpen && (
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleAddContents}
              color="primary"
              variant="contained"
              disabled={selectedContents.length === 0}
            >
              Add Selected ({selectedContents.length})
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default LPathContentSelector;